import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Wallet, AlertTriangle, CalendarClock, Calendar, TrendingDown,
  Landmark, Percent, Banknote, Search, RefreshCw, Plus, FileText,
  Loader2, ChevronRight, Building2, Zap, Smartphone, Car, Hammer,
  HeartPulse, Anchor, Home, MonitorSmartphone, MoreHorizontal,
  Receipt, X, Paperclip, BookOpen, ArrowRight, Activity, Sparkles,
} from 'lucide-react';

import PayBillModal from '../components/PayBillModal';
import VATClaimAuditTimeline from '../components/VATClaimAuditTimeline';

/**
 * Financial Operations Center — the unified hub.
 *
 * Sections:
 *   1) Smart Dashboard — 8 cards (cash · due-today · overdue · week
 *      · month · penalties · gov-deadlines · cash-gap).
 *   2) Smart Alerts — recommendation strip (traffic-fines escalation,
 *      cash gap, discount-at-risk, gov overdue).
 *   3) Filter row + Tab strip per bill_kind.
 *   4) Unified bills table — every payable obligation.
 *   5) Bill Workspace drawer — full drill-down on click (bill +
 *      attachments + payments + journals + bank-tx + audit).
 *
 * This page REPLACES the old Bills Center as the primary entry point.
 */
const fmt = (v) =>
  Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const KIND_META = {
  all:          { ar: 'الكُل',        icon: FileText,         tone: 'slate' },
  supplier:     { ar: 'مَوَرِّدين',     icon: Building2,        tone: 'indigo' },
  utility:      { ar: 'مَرافِق',       icon: Zap,              tone: 'amber' },
  telecom:      { ar: 'اتِّصالات',     icon: Smartphone,       tone: 'indigo' },
  government:   { ar: 'حُكومي',        icon: Landmark,         tone: 'emerald' },
  municipality: { ar: 'بَلَدِيَّة',     icon: Building2,        tone: 'teal' },
  traffic_fine: { ar: 'مُرور',         icon: Car,              tone: 'red' },
  customs:      { ar: 'جَمارِك',       icon: Anchor,           tone: 'blue' },
  insurance:    { ar: 'تَأمين',        icon: HeartPulse,       tone: 'red' },
  subscription: { ar: 'اشتِراكات',     icon: MonitorSmartphone,tone: 'indigo' },
  rent:         { ar: 'إيجار',         icon: Home,             tone: 'amber' },
  professional: { ar: 'مِهَني',        icon: Hammer,           tone: 'purple' },
  other:        { ar: 'أُخرى',         icon: MoreHorizontal,   tone: 'slate' },
};

const STATUS_BADGE = {
  draft:    'bg-slate-100 text-slate-700',
  received: 'bg-blue-100 text-blue-800',
  approved: 'bg-indigo-100 text-indigo-800',
  partial:  'bg-amber-100 text-amber-800',
  paid:     'bg-green-100 text-green-800',
  overdue:  'bg-red-100 text-red-800',
  cancelled:'bg-slate-100 text-slate-500 line-through',
};


// ── KPI card ─────────────────────────────────────────────────────


const KpiCard = ({ icon: Icon, label, primary, secondary, tone = 'slate', testid, onClick, active }) => {
  const tones = {
    indigo: 'border-indigo-300 bg-indigo-50/60 text-indigo-900 hover:bg-indigo-100',
    green:  'border-green-300 bg-green-50/60 text-green-900 hover:bg-green-100',
    red:    'border-red-300 bg-red-50/60 text-red-900 hover:bg-red-100',
    amber:  'border-amber-300 bg-amber-50/60 text-amber-900 hover:bg-amber-100',
    blue:   'border-blue-300 bg-blue-50/60 text-blue-900 hover:bg-blue-100',
    purple: 'border-purple-300 bg-purple-50/60 text-purple-900 hover:bg-purple-100',
    slate:  'border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
  }[tone];
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`text-start rounded-xl border p-3 transition-all ${tones} ${
        active ? 'ring-2 ring-offset-2 ring-indigo-500 shadow-md' : ''
      } disabled:cursor-default`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-xl font-bold mt-1.5 font-mono">{primary}</div>
      {secondary && <div className="text-[10px] mt-0.5 opacity-70">{secondary}</div>}
    </button>
  );
};


// ── Main page ────────────────────────────────────────────────────


export default function FinancialOperationsCenter({ user: _user }) {
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [dueFilter, setDueFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerBill, setDrawerBill] = useState(null);
  const [payingBill, setPayingBill] = useState(null);

  const loadDash = useCallback(async () => {
    try {
      const [{ data: d }, { data: a }] = await Promise.all([
        axiosInstance.get('/financial-ops/dashboard'),
        axiosInstance.get('/financial-ops/alerts'),
      ]);
      setDash(d);
      setAlerts(a.alerts || []);
    } catch {
      // best effort
    }
  }, []);

  const loadBills = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.bill_kind = activeTab;
      const { data } = await axiosInstance.get('/bills', { params });
      let arr = Array.isArray(data) ? data : (data.items || []);
      const today = new Date().toISOString().slice(0, 10);
      const cutoff = (days) => {
        const d = new Date(); d.setDate(d.getDate() + days);
        return d.toISOString().slice(0, 10);
      };
      if (dueFilter === 'overdue') {
        arr = arr.filter((b) => b.due_date && b.due_date < today && (b.amount_remaining ?? 0) > 0);
      } else if (dueFilter === 'due_today') {
        arr = arr.filter((b) => b.due_date === today && (b.amount_remaining ?? 0) > 0);
      } else if (dueFilter === 'due_this_week') {
        const w = cutoff(7);
        arr = arr.filter((b) => b.due_date && b.due_date >= today && b.due_date <= w);
      } else if (dueFilter === 'due_this_month') {
        const m = cutoff(30);
        arr = arr.filter((b) => b.due_date && b.due_date >= today && b.due_date <= m);
      }
      if (search) {
        const s = search.toLowerCase();
        arr = arr.filter((b) =>
          (b.bill_number || '').toLowerCase().includes(s) ||
          (b.supplier_name || '').toLowerCase().includes(s) ||
          (b.expense_category_name || '').toLowerCase().includes(s),
        );
      }
      setBills(arr);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تَعَذَّر جَلب الفَواتير');
    } finally {
      setLoading(false);
    }
  }, [activeTab, dueFilter, search]);

  useEffect(() => { loadDash(); }, [loadDash]);
  useEffect(() => { loadBills(); }, [loadBills]);

  const tabOrder = useMemo(
    () => ['all','supplier','utility','telecom','government','municipality',
           'traffic_fine','customs','insurance','rent','subscription','professional','other'],
    [],
  );
  const cards = dash?.cards || {};

  return (
    <div className="p-6 space-y-5" data-testid="financial-ops-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-indigo-600" />
            مَركَز العَمَلِيَّات المالية
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            لَوحَة قِيادَة مالية مُوَحَّدَة — مَوَرِّدين، حُكومي، مَرافِق، وَوَثائق في شاشَة واحِدَة فَوق مُحَرِّك مُحاسَبي مُوَحَّد.
          </p>
        </div>
        <a
          href="/government-services"
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold inline-flex items-center gap-2 hover:from-indigo-700 hover:to-purple-700"
          data-testid="foc-gov-services-btn"
        >
          <Landmark className="w-4 h-4" /> الخِدمات الحُكومية
        </a>
      </div>

      {/* Smart Dashboard — 8 cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="foc-dashboard-cards">
        <KpiCard
          icon={Wallet} label="الرَّصيد البَنكي المُتاح" tone="green"
          primary={`${fmt(cards.available_cash?.amount)} SAR`}
          secondary={`عَبر ${cards.available_cash?.banks ?? 0} بَنك`}
          testid="foc-card-cash"
        />
        <KpiCard
          icon={CalendarClock} label="مُستَحَق اليَوم" tone="amber"
          primary={cards.due_today?.count ?? 0}
          secondary={`${fmt(cards.due_today?.amount)} SAR`}
          active={dueFilter === 'due_today'}
          onClick={() => setDueFilter(dueFilter === 'due_today' ? null : 'due_today')}
          testid="foc-card-due-today"
        />
        <KpiCard
          icon={AlertTriangle} label="مُتَأَخِّر" tone="red"
          primary={cards.overdue?.count ?? 0}
          secondary={`${fmt(cards.overdue?.amount)} SAR`}
          active={dueFilter === 'overdue'}
          onClick={() => setDueFilter(dueFilter === 'overdue' ? null : 'overdue')}
          testid="foc-card-overdue"
        />
        <KpiCard
          icon={Calendar} label="مُستَحَق هَذا الأُسبوع" tone="blue"
          primary={cards.due_this_week?.count ?? 0}
          secondary={`${fmt(cards.due_this_week?.amount)} SAR`}
          active={dueFilter === 'due_this_week'}
          onClick={() => setDueFilter(dueFilter === 'due_this_week' ? null : 'due_this_week')}
          testid="foc-card-week"
        />
        <KpiCard
          icon={Banknote} label="غَرامات تَأخير مُتَوَقَّعَة" tone="red"
          primary={`${fmt(cards.expected_penalty?.amount)} SAR`}
          secondary={`${cards.expected_penalty?.gov_overdue ?? 0} فاتورة حُكومية`}
          testid="foc-card-penalty"
        />
        <KpiCard
          icon={Landmark} label="آخِر مَواعيد حُكومية / 7 أَيام" tone="purple"
          primary={cards.gov_deadlines_7d?.count ?? 0}
          secondary="مُخالَفات/رُسوم تَنتَهي قَريباً"
          testid="foc-card-gov-deadlines"
        />
        <KpiCard
          icon={TrendingDown} label="عَجز نَقدي / 30 يَوم" tone={(cards.cash_gap_30d?.amount ?? 0) > 0 ? 'red' : 'green'}
          primary={`${fmt(Math.abs(cards.cash_gap_30d?.amount ?? 0))} SAR`}
          secondary={(cards.cash_gap_30d?.amount ?? 0) > 0 ? 'فَجوَة فِعلية' : 'تَدَفُّق سَليم'}
          testid="foc-card-cash-gap"
        />
        <KpiCard
          icon={Sparkles} label="الرَّصيد بَعد السَّداد الفَوري" tone="indigo"
          primary={`${fmt(cards.projected_balance?.amount)} SAR`}
          secondary={`بَعد سَداد ${fmt(cards.projected_balance?.if_pay_now)} SAR`}
          testid="foc-card-projected"
        />
      </div>

      {/* Smart Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="foc-alerts">
          {alerts.map((a) => {
            const tones = {
              high:   'border-red-300 bg-red-50 text-red-900',
              medium: 'border-amber-300 bg-amber-50 text-amber-900',
              info:   'border-blue-300 bg-blue-50 text-blue-900',
            }[a.severity] || 'border-slate-300 bg-slate-50 text-slate-900';
            return (
              <a
                key={a.id}
                href={a.action_path || '#'}
                data-testid={`foc-alert-${a.id}`}
                className={`block rounded-lg border p-3 ${tones} hover:shadow-md transition-shadow`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold">{a.title_ar}</div>
                  {a.action_ar && (
                    <span className="text-xs px-2 py-1 rounded-md bg-white/60 font-bold">
                      {a.action_ar} <ArrowRight className="w-3 h-3 inline" />
                    </span>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      )}

      {/* Tabs per bill_kind */}
      <div className="bg-white rounded-xl border border-slate-200 p-2 flex items-center gap-1 overflow-x-auto" data-testid="foc-tabs">
        {tabOrder.map((k) => {
          const meta = KIND_META[k];
          const Icon = meta.icon;
          const isActive = activeTab === k;
          return (
            <button
              key={k} onClick={() => setActiveTab(k)}
              data-testid={`foc-tab-${k}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 whitespace-nowrap transition-all ${
                isActive
                  ? `bg-${meta.tone}-100 text-${meta.tone}-800 ring-1 ring-${meta.tone}-300`
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {meta.ar}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200 flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="foc-search"
            placeholder="بَحث بِرَقم الفاتورة، اسم الجِهَة، أَو التَّصنيف..."
            className="bg-transparent border-0 outline-none text-sm flex-1"
          />
        </div>
        <button onClick={() => { loadBills(); loadDash(); }}
          data-testid="foc-refresh"
          className="px-3 py-1.5 rounded-lg text-xs bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> تَحديث
        </button>
        {dueFilter && (
          <button onClick={() => setDueFilter(null)}
            className="px-3 py-1.5 rounded-lg text-xs bg-red-50 text-red-700 border border-red-200">
            مَسح فِلتر
          </button>
        )}
      </div>

      {/* Unified bills table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="foc-bills-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="p-2 text-start">النَّوع</th>
                <th className="p-2 text-start">رَقم</th>
                <th className="p-2 text-start">الجِهَة</th>
                <th className="p-2 text-start">التَّصنيف</th>
                <th className="p-2 text-start">الاستِحقاق</th>
                <th className="p-2 text-end">القيمة</th>
                <th className="p-2 text-end">VAT</th>
                <th className="p-2 text-end">المُتَبَقّي</th>
                <th className="p-2 text-center">الحالة</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="p-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline me-2" /> جارٍ التَّحميل...
                </td></tr>
              ) : bills.length === 0 ? (
                <tr><td colSpan="10" className="p-8 text-center text-slate-500">
                  لا تُوجَد فَواتير مُطابِقَة.
                </td></tr>
              ) : bills.map((b) => {
                const meta = KIND_META[b.bill_kind || 'supplier'] || KIND_META.other;
                const Icon = meta.icon;
                const today = new Date().toISOString().slice(0, 10);
                const overdue = b.due_date && b.due_date < today && (b.amount_remaining ?? 0) > 0;
                const remaining = b.amount_remaining ?? 0;
                return (
                  <tr key={b.id} data-testid={`foc-row-${b.id}`}
                    className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${overdue ? 'bg-red-50/30' : ''}`}
                    onClick={() => setDrawerBill(b)}>
                    <td className="p-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-${meta.tone}-100 text-${meta.tone}-800`}>
                        <Icon className="w-3 h-3" /> {meta.ar}
                      </span>
                    </td>
                    <td className="p-2 font-mono text-xs text-slate-900">{b.bill_number}</td>
                    <td className="p-2 text-xs text-slate-700">{b.supplier_name || '—'}</td>
                    <td className="p-2 text-xs text-slate-500">{b.expense_category_name || '—'}</td>
                    <td className={`p-2 text-xs ${overdue ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>
                      {b.due_date || '—'}
                    </td>
                    <td className="p-2 text-end font-mono text-xs">{fmt(b.total)}</td>
                    <td className="p-2 text-end font-mono text-xs text-indigo-700">{fmt(b.vat_amount)}</td>
                    <td className={`p-2 text-end font-mono text-xs font-semibold ${remaining > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      {fmt(remaining)}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_BADGE[b.status] || 'bg-slate-100 text-slate-700'}`}>
                        {b.status || 'draft'}
                      </span>
                    </td>
                    <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                      {remaining > 0 && (
                        <button
                          onClick={() => setPayingBill(b)}
                          data-testid={`foc-pay-${b.id}`}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold inline-flex items-center gap-0.5"
                        >
                          دَفع <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {drawerBill && (
        <BillWorkspaceDrawer
          bill={drawerBill}
          onClose={() => setDrawerBill(null)}
          onPay={() => { setPayingBill(drawerBill); setDrawerBill(null); }}
        />
      )}
      {payingBill && (
        <PayBillModal
          bill={payingBill}
          onClose={() => setPayingBill(null)}
          onPaid={() => { setPayingBill(null); loadBills(); loadDash(); toast.success('تَم الدَّفع'); }}
        />
      )}
    </div>
  );
}


// ─── Bill Workspace Drawer ───────────────────────────────────────


const BillWorkspaceDrawer = ({ bill, onClose, onPay }) => {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get(`/financial-ops/bills/${bill.id}/workspace`);
        setWorkspace(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'تَعَذَّر جَلب التَّفاصيل');
      } finally {
        setLoading(false);
      }
    })();
  }, [bill.id]);

  const remaining = workspace?.bill?.amount_remaining ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" data-testid="foc-bill-workspace" role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto flex flex-col">
        <div className="p-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white sticky top-0 z-10 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5" /> {workspace?.bill?.bill_number || bill.bill_number}
            </h3>
            <p className="text-xs opacity-90 mt-0.5">
              {workspace?.bill?.supplier_name || bill.supplier_name} ·{' '}
              {workspace?.bill?.expense_category_name || bill.expense_category_name || '—'}
            </p>
          </div>
          <button onClick={onClose}
            data-testid="foc-bill-workspace-close"
            className="hover:bg-white/20 rounded-lg p-1.5"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin inline" />
          </div>
        ) : workspace ? (
          <div className="p-5 space-y-4 flex-1">
            {/* Quick numbers */}
            <div className="grid grid-cols-4 gap-2">
              <Stat label="القيمَة" value={`${fmt(workspace.bill.total)} SAR`} />
              <Stat label="VAT" value={`${fmt(workspace.bill.vat_amount)} SAR`} tone="indigo" />
              <Stat label="مَدفوع" value={`${fmt(workspace.bill.amount_paid)} SAR`} tone="green" />
              <Stat label="مُتَبَقِّي" value={`${fmt(remaining)} SAR`} tone={remaining > 0 ? 'amber' : 'green'} />
            </div>

            {/* Bill metadata */}
            <Panel title="بيانات الفاتورة" icon={FileText}>
              <Row k="تاريخ الفاتورة" v={workspace.bill.bill_date} />
              <Row k="تاريخ الاستِحقاق" v={workspace.bill.due_date} />
              <Row k="النَّوع" v={workspace.bill.bill_kind} />
              <Row k="حالة الفاتورة" v={workspace.bill.status} />
              <Row k="حالة الاعتِماد" v={workspace.bill.approval_status} />
              <Row k="فَترة مُطالَبة VAT" v={workspace.bill.vat_claim_period} />
            </Panel>

            {/* Expense Category */}
            {workspace.expense_category && (
              <Panel title="التَّصنيف الذَّكي" icon={Sparkles}>
                <Row k="الكود" v={workspace.expense_category.code} />
                <Row k="الاسم" v={workspace.expense_category.name_ar || workspace.expense_category.name_en} />
                <Row k="حِساب افتِراضي" v={workspace.expense_category.default_account_code} />
                <Row k="VAT %" v={workspace.expense_category.default_vat_rate + '%'} />
              </Panel>
            )}

            {/* Payments */}
            <Panel title={`المَدفوعات (${(workspace.payments || []).length})`} icon={Banknote}>
              {(workspace.payments || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic">لا تُوجَد مَدفوعات بَعد.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr><th className="text-start py-1">رَقم</th><th className="text-start">تاريخ</th><th className="text-end">المَبلَغ</th><th className="text-center">طَريقَة</th></tr>
                  </thead>
                  <tbody>
                    {workspace.payments.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100">
                        <td className="py-1 font-mono">{p.payment_number}</td>
                        <td>{p.payment_date}</td>
                        <td className="text-end font-mono">{fmt(p.amount)}</td>
                        <td className="text-center">{p.payment_method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            {/* Bank Transactions */}
            <Panel title={`حَرَكات بَنكِيَّة (${(workspace.bank_transactions || []).length})`} icon={Wallet}>
              {(workspace.bank_transactions || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic">لا تُوجَد حَرَكات بَنكِيَّة مَربوطَة.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-slate-500"><tr><th className="text-start py-1">رَقم</th><th className="text-start">تاريخ</th><th className="text-end">المَبلَغ</th></tr></thead>
                  <tbody>
                    {workspace.bank_transactions.map((t) => (
                      <tr key={t.id} className="border-t border-slate-100">
                        <td className="py-1 font-mono">{t.transaction_number}</td>
                        <td>{t.transaction_date}</td>
                        <td className="text-end font-mono">{fmt(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            {/* Journals */}
            <Panel title={`القُيود المُحاسَبية (${(workspace.journals || []).length})`} icon={BookOpen}>
              {(workspace.journals || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic">لا تُوجَد قُيود مَربوطَة.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-slate-500"><tr><th className="text-start py-1">رَقم</th><th className="text-start">تاريخ</th><th className="text-end">قيمَة</th></tr></thead>
                  <tbody>
                    {workspace.journals.map((j) => (
                      <tr key={j.id} className="border-t border-slate-100">
                        <td className="py-1 font-mono">{j.journal_no || j.reference}</td>
                        <td>{j.date}</td>
                        <td className="text-end font-mono">{fmt(j.total_debit || j.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            {/* Attachments */}
            <Panel title={`المُرفَقات (${(workspace.attachments || []).length})`} icon={Paperclip}>
              {(workspace.attachments || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic">لا تُوجَد مُرفَقات بَعد.</div>
              ) : (
                <ul className="space-y-1 text-xs">
                  {workspace.attachments.map((a, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <Paperclip className="w-3 h-3 text-slate-400" />
                      <a href={a.url || a.path} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                        {a.name || a.filename || `attachment-${i + 1}`}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* VAT Audit */}
            <Panel title="سِجِل مُطالَبة VAT" icon={Receipt}>
              <VATClaimAuditTimeline documentId={workspace.bill.id} documentType="supplier_bill" />
            </Panel>
          </div>
        ) : null}

        {/* Footer with Pay button */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 sticky bottom-0 flex items-center justify-between gap-2">
          <div className="text-sm text-slate-700">
            <span className="text-xs text-slate-500">المُتَبَقّي:</span>{' '}
            <span className="font-mono font-bold text-amber-700">{fmt(remaining)} SAR</span>
          </div>
          {remaining > 0 && (
            <button
              onClick={onPay}
              data-testid="foc-workspace-pay-btn"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold inline-flex items-center gap-2 hover:from-indigo-700"
            >
              <Banknote className="w-4 h-4" /> دَفع الفاتورة
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


const Panel = ({ title, icon: Icon, children }) => (
  <div className="rounded-lg border border-slate-200 bg-white">
    <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 text-xs font-semibold text-slate-700">
      <Icon className="w-3.5 h-3.5" /> {title}
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
    <span className="text-slate-500">{k}</span>
    <span className="font-mono text-slate-900">{v ?? '—'}</span>
  </div>
);

const Stat = ({ label, value, tone = 'slate' }) => {
  const tones = {
    slate:  'bg-slate-50 text-slate-700',
    indigo: 'bg-indigo-50 text-indigo-900',
    green:  'bg-green-50 text-green-900',
    amber:  'bg-amber-50 text-amber-900',
  };
  return (
    <div className={`rounded-lg p-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="font-mono font-bold text-sm mt-0.5">{value}</div>
    </div>
  );
};
